import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Trash2 } from 'lucide-react';
import { notificationService } from '@/services/notificationService';
import { useAuth } from '@/contexts/AuthContext';

interface PipelineAssignment {
  id: number;
  pipeline_id: number;
  user_id: string;
  assignment_weight: number;
  is_active: boolean;
  last_assigned_at: string | null;
  user?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
}

interface PipelineAssignmentsProps {
  pipelineId: number;
  pipelineName?: string;
}

export function PipelineAssignments({ pipelineId, pipelineName }: PipelineAssignmentsProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<PipelineAssignment[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [assignmentWeight, setAssignmentWeight] = useState<number>(50);
  const [isEvenSplit, setIsEvenSplit] = useState<boolean>(true);

  useEffect(() => {
    fetchAssignments();
    fetchUsers();
  }, [pipelineId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('pipeline_assignments')
        .select(`*, user:user_id (id, first_name, last_name, email)`) // join user
        .eq('pipeline_id', pipelineId)
        .order('assignment_weight', { ascending: false });
      if (error) throw error;
      setAssignments(data || []);
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to load assignments", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true });
      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to load users", variant: "destructive" });
    }
  };

  const handleAddAssignment = async () => {
    if (!selectedUser) {
      toast({
        title: "Error",
        description: "Please select a user",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if user already has an assignment for this pipeline
      const { data: existingAssignments, error: checkError } = await supabase
        .from('pipeline_assignments')
        .select('id')
        .eq('pipeline_id', pipelineId)
        .eq('user_id', selectedUser);

      if (checkError) throw checkError;

      if (existingAssignments && existingAssignments.length > 0) {
        toast({
          title: "Error",
          description: "This user already has an assignment for this pipeline",
          variant: "destructive",
        });
        return;
      }

      const tenantId = await getCurrentTenantId();
      
      const { error } = await supabase
        .from('pipeline_assignments')
        .insert({
          user_id: selectedUser,
          pipeline_id: pipelineId,
          assignment_weight: assignmentWeight,
          is_active: true,
          tenant_id: tenantId
        });

      if (error) throw error;

      // Fetch current user's name from database for notification
      let senderFullName = 'System';
      if (user?.id) {
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          if (!userError && userData) {
            senderFullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'System';
          }
        } catch (err) {
          console.error('[PipelineAssignments] Error fetching user name for notification:', err);
        }
      }

      // Send notification to the assigned user
      await notificationService.createNotification({
        user_id: selectedUser,
        type: 'project_assignment',
        title: `Pipeline Assignment: ${pipelineName}`,
        message: `You have been assigned to the ${pipelineName} pipeline with ${assignmentWeight}% assignment weight.`,
        metadata: {
          pipeline_id: pipelineId,
          pipeline_name: pipelineName,
          assignment_weight: assignmentWeight
        },
        sender_id: user?.id,
        sender_name: senderFullName,
        link: `/app/pipeline?pipelineId=${pipelineId}`
      });

      toast({
        title: "Success",
        description: "Assignment added successfully",
      });

      fetchAssignments();
      setSelectedUser('');
      setAssignmentWeight(1);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add assignment",
        variant: "destructive",
      });
    }
  };

  const handleUpdateWeight = async (assignmentId: number, weight: number) => {
    try {
      const { error } = await supabase
        .from('pipeline_assignments')
        .update({ assignment_weight: weight })
        .eq('id', assignmentId);
      if (error) throw error;
      toast({ title: "Success", description: "Assignment weight updated" });
      fetchAssignments();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to update assignment", variant: "destructive" });
    }
  };

  const handleToggleActive = async (assignmentId: number, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('pipeline_assignments')
        .update({ is_active: !isActive })
        .eq('id', assignmentId);
      if (error) throw error;
      toast({ title: "Success", description: `Assignment ${isActive ? 'deactivated' : 'activated'}` });
      fetchAssignments();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to update assignment", variant: "destructive" });
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    try {
      const { error } = await supabase
        .from('pipeline_assignments')
        .delete()
        .eq('id', assignmentId);
      if (error) throw error;
      toast({ title: "Success", description: "Assignment removed" });
      fetchAssignments();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete assignment", variant: "destructive" });
    }
  };

  return (
    <section className="mt-6">
      <hr className="mb-4" />
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">Project Assignments</h3>
        <div className="flex items-center space-x-2">
          <Label htmlFor="even-split">Even Split</Label>
          <Switch
            id="even-split"
            checked={isEvenSplit}
            onCheckedChange={setIsEvenSplit}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex-1 min-w-[180px]">
          <Label className="mb-1 block">Add User</Label>
          <SearchableSelect
            id="user"
            value={selectedUser}
            onValueChange={setSelectedUser}
            options={users.map(user => ({
              label: `${user.first_name} ${user.last_name}`,
              value: user.id
            }))}
            placeholder="Select a user"
          />
        </div>
        {!isEvenSplit && (
          <div className="w-24">
            <Label className="mb-1 block">Weight %</Label>
            <Input
              id="weight-input"
              type="number"
              min="1"
              max="100"
              value={assignmentWeight}
              onChange={(e) => setAssignmentWeight(parseInt(e.target.value) || 1)}
            />
          </div>
        )}
        <Button onClick={handleAddAssignment} className="h-10 bg-primary hover:bg-primaryHover text-white font-semibold px-6">
          Add
        </Button>
      </div>
      {isLoading ? (
        <div className="py-4 text-center">Loading assignments...</div>
      ) : assignments.length === 0 ? (
        <div className="py-4 text-center text-muted-foreground">No assignments found</div>
      ) : (
        <div className="flex flex-col gap-2">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
              <div className="flex-1">
                <p className="font-medium">
                  {assignment.user?.first_name} {assignment.user?.last_name}
                </p>
                <p className="text-sm text-muted-foreground">{assignment.user?.email}</p>
              </div>
              <div className="flex items-center gap-4">
                {!isEvenSplit && (
                  <div className="w-20">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={assignment.assignment_weight}
                      onChange={(e) => handleUpdateWeight(assignment.id, parseInt(e.target.value) || 1)}
                      className="h-8"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch
                    checked={assignment.is_active}
                    onCheckedChange={() => handleToggleActive(assignment.id, assignment.is_active)}
                  />
                  <span className="text-sm text-muted-foreground">
                    {assignment.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteAssignment(assignment.id)}
                  className="text-destructive hover:text-destructive"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
} 